import React, { createContext, useContext, useEffect, useMemo } from "react";
import { createInstance } from "i18next";
import { I18nextProvider, initReactI18next, useTranslation } from "react-i18next";
import { DirectionProvider } from "@radix-ui/react-direction";
import { format as formatDate } from "date-fns";
import { arSA, de as deDate, enUS, es as esDate, fr as frDate, it as itDate, zhCN } from "date-fns/locale";
import en from "../locales/en.json";
import zh from "../locales/zh.json";
import fr from "../locales/fr.json";
import it from "../locales/it.json";
import de from "../locales/de.json";
import es from "../locales/es.json";
import ar from "../locales/ar.json";

export const LANGUAGE_STORAGE_KEY = "voocly_partner_language";
export const SUPPORTED_LANGUAGES = ["en", "zh", "fr", "it", "de", "es", "ar"];
export const normalizeLanguage = (value) => SUPPORTED_LANGUAGES.includes(value) ? value : "en";
const storedLanguage = () => {
  try { return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY)); }
  catch { return "en"; }
};
export const partnerI18n = createInstance();
partnerI18n.use(initReactI18next).init({
  resources: Object.fromEntries(Object.entries({ en, zh, fr, it, de, es, ar })
    .map(([language, translation]) => [language, { translation }])),
  lng: storedLanguage(), fallbackLng: "en", supportedLngs: SUPPORTED_LANGUAGES,
  keySeparator: false, nsSeparator: false,
  interpolation: { escapeValue: false }, returnEmptyString: false,
  react: { useSuspense: false },
});
/** Translate authored interface text; never apply this to supplier-entered content. */
export function tr(key, fallbackOrOptions) {
  if (typeof key !== "string") return key;
  const fallback = typeof fallbackOrOptions === "string" ? fallbackOrOptions : undefined;
  const options = typeof fallbackOrOptions === "object" ? fallbackOrOptions : {};
  if (fallback && !partnerI18n.exists(key, { lng: partnerI18n.language, fallbackLng: false })) {
    return partnerI18n.t(fallback, { defaultValue: fallback, ...options });
  }
  return partnerI18n.t(key, { defaultValue: fallback || key, ...options });
}
/** Subscribe components without resetting unsaved form state. */
export function usePartnerTranslation() {
  return useTranslation(undefined, { i18n: partnerI18n, useSuspense: false });
}
const LanguageContext = createContext(null);
export function LanguageProvider({ children }) {
  const { i18n } = usePartnerTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const direction = language === "ar" ? "rtl" : "ltr";
  useEffect(() => {
    try { localStorage.setItem(LANGUAGE_STORAGE_KEY, language); } catch { /* Storage may be unavailable. */ }
    document.documentElement.lang = language === "zh" ? "zh-Hans" : language;
    document.documentElement.dir = direction;
  }, [language, direction]);
  const value = useMemo(() => ({ language, direction,
    setLanguage: (next) => i18n.changeLanguage(normalizeLanguage(next)), t: tr,
  }), [i18n, language, direction]);
  return (
    <I18nextProvider i18n={partnerI18n}>
      <LanguageContext.Provider value={value}>
        <DirectionProvider dir={direction}>{children}</DirectionProvider>
      </LanguageContext.Provider>
    </I18nextProvider>
  );
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  const { i18n } = usePartnerTranslation();
  return context || { language: normalizeLanguage(i18n.resolvedLanguage || i18n.language),
    setLanguage: (next) => i18n.changeLanguage(normalizeLanguage(next)), t: tr };
}

export const partnerDateLocale = () => ({ ar: arSA, de: deDate, en: enUS, es: esDate, fr: frDate, it: itDate, zh: zhCN }[normalizeLanguage(partnerI18n.language)]);
export function formatPartnerDate(date, pattern, options) {
  return formatDate(date, pattern.replace(" 'at' ", " · "), { locale: partnerDateLocale(), ...options });
}
export const partnerLocale = () => partnerI18n.language === "zh" ? "zh-CN" : normalizeLanguage(partnerI18n.language);
